/**
 * Phase 1E.2B — Admin Sticker Database + Importer UI.
 *
 * Tabs:
 *   Hierarchy · Categories · Stickers · Checklist Groups · Variant Checklists · Importer
 *
 * All CRUD is admin-cookie authenticated via the shared `api` client.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, Building2, Trophy, CalendarDays, ChevronRight,
         Plus, Trash2, Pencil, RefreshCw, Upload, ListChecks,
         Package, Layers, X, CheckCircle2, AlertCircle, Image,
         ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

// Batch 2 — Item-type dropdown options. Values must match the backend
// `ITEM_TYPES` set in `routers/catalog.py`.
const ITEM_TYPES = [
  { value: "album",                label: "Album" },
  { value: "album_variant",        label: "Album Variant" },
  { value: "box_or_set",           label: "Box or Set" },
  { value: "special_sticker",      label: "Special Sticker" },
  { value: "premium_collectible",  label: "Premium Collectible" },
  { value: "other",                label: "Other" },
];
const ITEM_TYPE_LABEL = Object.fromEntries(ITEM_TYPES.map((t) => [t.value, t.label]));

const TABS = [
  { id: "hierarchy",    label: "Catalog Manager",   icon: Building2 },
  { id: "categories",   label: "Categories",        icon: ListChecks },
  { id: "stickers",     label: "Stickers",          icon: Package },
  { id: "groups",       label: "Checklist Groups",  icon: Layers },
  { id: "variants",     label: "Variant Checklists", icon: Trophy },
  { id: "covers",       label: "Cover Images",      icon: Image },
  { id: "importer",     label: "Importer",          icon: Upload },
];

export default function AdminCatalog() {
  const [tab, setTab] = useState("hierarchy");
  const Icon = TABS.find(t => t.id === tab)?.icon || Building2;

  return (
    <div className="space-y-6" data-testid="admin-catalog">
      <div>
        <h2 className="font-display text-3xl">Catalog Manager</h2>
        <p className="text-sm text-[var(--smoke)] mt-1">
          Manage every item collectors can add to My Collection — albums, album variants,
          special stickers, treasure boxes, and other premium collectibles. Every price
          and completion status is still driven by the existing Master Sticker Database.
        </p>
      </div>

      <div className="flex gap-1 border-b-2 border-[var(--ink)] overflow-x-auto" role="tablist">
        {TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            data-testid={`catalog-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={"px-4 py-2 text-sm font-bold uppercase tracking-wider flex items-center gap-2 whitespace-nowrap " +
              (tab === t.id
                ? "bg-[var(--ink)] text-white"
                : "bg-white text-[var(--ink)] hover:bg-neutral-100")}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "hierarchy" && <HierarchyTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "stickers"   && <StickersTab />}
      {tab === "groups"     && <ChecklistGroupsTab />}
      {tab === "variants"   && <VariantChecklistsTab />}
      {tab === "covers"     && <CoverImagesTab />}
      {tab === "importer"   && <ImporterTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Catalog Manager — replaces the read-only hierarchy browser with a
// full interactive editor for families, releases, and every catalog
// item (album, album variant, box/set, special sticker, premium
// collectible). Batch 2 owner ask (Msg-Batch2):
//   • Public display names must render alongside internal codes.
//   • Visibility (active + published) toggleable inline.
//   • Move up / move down reorders siblings by display_order.
//   • Item-type dropdown supports non-album collectibles.
//   • Completion-tracking toggle lets special stickers skip the
//     "0% complete" percentage.
//   • Hidden items still appear to admin. They are already excluded
//     from the public /api/catalog/* endpoints that back My Collection.
// ═══════════════════════════════════════════════════════════════════
function HierarchyTab() {
  const [mfrs, setMfrs] = useState([]);
  const [families, setFamilies] = useState({});
  const [releases, setReleases] = useState({});
  const [variants, setVariants] = useState({});
  const [loading, setLoading] = useState(true);
  const [visibility, setVisibility] = useState("all"); // all | visible | hidden
  const [editing, setEditing] = useState(null); // { kind, item, parentId? }
  const [creating, setCreating] = useState(null); // { kind, releaseId }

  const load = async () => {
    setLoading(true);
    try {
      const m = await api.get("/admin/catalog/manufacturers");
      setMfrs(m.data.items);
      const famsByM = {};
      for (const mfr of m.data.items) {
        const f = await api.get("/admin/catalog/collection-families",
                                { params: { manufacturer_id: mfr.id } });
        famsByM[mfr.id] = f.data.items;
      }
      setFamilies(famsByM);
      const relsByF = {};
      for (const list of Object.values(famsByM)) {
        for (const fam of list) {
          const r = await api.get("/admin/catalog/releases",
                                   { params: { collection_family_id: fam.id } });
          relsByF[fam.id] = r.data.items;
        }
      }
      setReleases(relsByF);
      const varsByR = {};
      for (const list of Object.values(relsByF)) {
        for (const rel of list) {
          const v = await api.get("/admin/catalog/album-variants",
                                   { params: { release_id: rel.id } });
          varsByR[rel.id] = v.data.items;
        }
      }
      setVariants(varsByR);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  function matchesVisibility(row, mode) {
    const visible = row.active !== false && row.published !== false;
    if (mode === "visible") return visible;
    if (mode === "hidden") return !visible;
    return true;
  }

  async function move(kind, id, direction) {
    try {
      const key = { family: "families", release: "releases", variant: "variants" }[kind];
      await api.post(`/admin/catalog/${key}/${id}/move-${direction}`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e) || `Move ${direction} failed`);
    }
  }

  async function togglePublished(kind, item) {
    try {
      const path = { family: "collection-families",
                     release: "releases", variant: "album-variants" }[kind];
      const nextPublished = !(item.published !== false);
      // Families don't have a `published` flag — they use `active` only.
      const patch = kind === "family"
        ? { active: !(item.active !== false) }
        : { published: nextPublished };
      await api.patch(`/admin/catalog/${path}/${item.id}`, patch);
      toast.success(kind === "family"
        ? `${item.public_display_name || item.name} ${patch.active ? "shown" : "hidden"}`
        : `${item.public_display_name || item.name} ${nextPublished ? "published" : "hidden"}`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e) || "Toggle failed");
    }
  }

  if (loading) return <SpinBox testid="hierarchy-loading" />;
  return (
    <div className="space-y-4" data-testid="catalog-manager">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm text-[var(--smoke)]">
          Master catalog for every item collectors may add to their My Collection.
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-[var(--smoke)]">Show</label>
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm bg-white"
            data-testid="catalog-visibility-filter"
          >
            <option value="all">All</option>
            <option value="visible">Visible only</option>
            <option value="hidden">Hidden only</option>
          </select>
          <button onClick={load} className="text-sm underline" data-testid="catalog-refresh">
            <RefreshCw size={12} className="inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      {mfrs.map((m) => (
        <div key={m.id} className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
          <div className={"px-5 py-3 flex items-center gap-3 " +
                          (m.active ? "bg-[var(--panini-yellow-soft)]" : "bg-neutral-100")}>
            <Building2 size={18} />
            <span className="font-display text-lg">{m.name}</span>
            <span className="text-xs font-mono text-[var(--smoke)]">{m.code}</span>
          </div>
          <div className="p-4 space-y-3">
            {(families[m.id] || [])
              .filter((f) => matchesVisibility(f, visibility))
              .map((f, fIdx, fArr) => (
              <div key={f.id} className="border border-neutral-300 rounded-xl">
                <div className="px-4 py-2 flex items-center gap-2 bg-neutral-50">
                  <Trophy size={14} />
                  <span className="font-bold" data-testid={`family-name-${f.code}`}>
                    {f.public_display_name || f.name}
                  </span>
                  <span className="text-xs font-mono text-[var(--smoke)]">{f.code}</span>
                  <VisibilityBadge active={f.active} published={true} />
                  <div className="ml-auto flex items-center gap-1">
                    <IconBtn title="Move up" disabled={fIdx === 0}
                             onClick={() => move("family", f.id, "up")}
                             testid={`family-up-${f.code}`}><ArrowUp size={12}/></IconBtn>
                    <IconBtn title="Move down" disabled={fIdx === fArr.length - 1}
                             onClick={() => move("family", f.id, "down")}
                             testid={`family-down-${f.code}`}><ArrowDown size={12}/></IconBtn>
                    <IconBtn title={f.active ? "Hide" : "Show"}
                             onClick={() => togglePublished("family", f)}
                             testid={`family-toggle-${f.code}`}>
                      {f.active ? <EyeOff size={12}/> : <Eye size={12}/>}
                    </IconBtn>
                    <IconBtn title="Edit" onClick={() => setEditing({ kind: "family", item: f })}
                             testid={`family-edit-${f.code}`}><Pencil size={12}/></IconBtn>
                  </div>
                </div>
                <div className="p-3 space-y-3">
                  {(releases[f.id] || [])
                    .filter((r) => matchesVisibility(r, visibility))
                    .map((r, rIdx, rArr) => (
                    <div key={r.id} className="border border-neutral-200 rounded-lg">
                      <div className="px-3 py-2 flex flex-wrap items-center gap-2 bg-neutral-50/60">
                        <CalendarDays size={14} />
                        <span className="font-display" data-testid={`release-name-${r.code}`}>
                          {r.public_display_name || r.name}
                        </span>
                        <span className="text-xs font-mono text-[var(--smoke)]">{r.code}</span>
                        <VisibilityBadge active={r.active} published={r.published} />
                        <div className="ml-auto flex items-center gap-1">
                          <IconBtn title="Move up" disabled={rIdx === 0}
                                   onClick={() => move("release", r.id, "up")}
                                   testid={`release-up-${r.code}`}><ArrowUp size={12}/></IconBtn>
                          <IconBtn title="Move down" disabled={rIdx === rArr.length - 1}
                                   onClick={() => move("release", r.id, "down")}
                                   testid={`release-down-${r.code}`}><ArrowDown size={12}/></IconBtn>
                          <IconBtn title={r.published ? "Hide from public" : "Publish"}
                                   onClick={() => togglePublished("release", r)}
                                   testid={`release-toggle-${r.code}`}>
                            {r.published ? <EyeOff size={12}/> : <Eye size={12}/>}
                          </IconBtn>
                          <IconBtn title="Edit" onClick={() => setEditing({ kind: "release", item: r })}
                                   testid={`release-edit-${r.code}`}><Pencil size={12}/></IconBtn>
                          <button onClick={() => setCreating({ kind: "variant", releaseId: r.id, release: r })}
                                  className="text-[10px] border-2 border-[var(--ink)] rounded-full px-2 py-0.5 hover:bg-[var(--panini-yellow-soft)]"
                                  data-testid={`variant-new-${r.code}`}>
                            <Plus size={10} className="inline"/> Add item
                          </button>
                        </div>
                      </div>
                      <div className="p-2 divide-y divide-neutral-100">
                        {(variants[r.id] || [])
                          .filter((v) => matchesVisibility(v, visibility))
                          .map((v, vIdx, vArr) => (
                          <VariantRow key={v.id} v={v} idx={vIdx} total={vArr.length}
                            onMove={(dir) => move("variant", v.id, dir)}
                            onToggle={() => togglePublished("variant", v)}
                            onEdit={() => setEditing({ kind: "variant", item: v })}
                          />
                        ))}
                        {(variants[r.id] || []).filter((v) => matchesVisibility(v, visibility)).length === 0 && (
                          <div className="text-xs text-[var(--smoke)] px-2 py-3">No items yet.</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <EditItemDialog
          kind={editing.kind} item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {creating && (
        <EditItemDialog
          kind={creating.kind}
          item={{ release_id: creating.releaseId, active: true, published: false,
                  item_type: "album_variant", completion_tracking_enabled: true }}
          releaseContext={creating.release}
          onClose={() => setCreating(null)}
          onSaved={async () => { setCreating(null); await load(); }}
        />
      )}
    </div>
  );
}

function VariantRow({ v, idx, total, onMove, onToggle, onEdit }) {
  const type = ITEM_TYPE_LABEL[v.item_type] || "Album Variant";
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-2 py-2 text-sm"
         data-testid={`variant-row-${v.code}`}>
      <div className="col-span-12 md:col-span-4 flex items-center gap-2 min-w-0">
        <span className="font-bold truncate" data-testid={`variant-name-${v.code}`}>
          {v.public_display_name || v.name}
        </span>
        <span className="text-[10px] font-mono text-[var(--smoke)]">{v.code}</span>
      </div>
      <div className="col-span-6 md:col-span-2 text-[11px] uppercase tracking-widest text-[var(--smoke)]">
        {type}
      </div>
      <div className="col-span-6 md:col-span-1 text-[11px] font-mono text-[var(--smoke)]">
        #{v.display_order ?? 0}
      </div>
      <div className="col-span-6 md:col-span-2 text-[11px] text-[var(--smoke)]">
        {v.completion_tracking_enabled === false
          ? <span className="text-amber-700">Ownership only</span>
          : <span>Tracks % complete</span>}
      </div>
      <div className="col-span-6 md:col-span-1">
        <VisibilityBadge active={v.active} published={v.published} />
      </div>
      <div className="col-span-12 md:col-span-2 flex items-center gap-1 justify-end">
        <IconBtn title="Move up" disabled={idx === 0}
                 onClick={() => onMove("up")}
                 testid={`variant-up-${v.code}`}><ArrowUp size={12}/></IconBtn>
        <IconBtn title="Move down" disabled={idx === total - 1}
                 onClick={() => onMove("down")}
                 testid={`variant-down-${v.code}`}><ArrowDown size={12}/></IconBtn>
        <IconBtn title={v.published ? "Hide" : "Publish"}
                 onClick={onToggle}
                 testid={`variant-toggle-${v.code}`}>
          {v.published ? <EyeOff size={12}/> : <Eye size={12}/>}
        </IconBtn>
        <IconBtn title="Edit" onClick={onEdit}
                 testid={`variant-edit-${v.code}`}><Pencil size={12}/></IconBtn>
      </div>
    </div>
  );
}

function VisibilityBadge({ active, published }) {
  const isVisible = active !== false && published !== false;
  return isVisible
    ? <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">Visible</span>
    : <span className="text-[10px] uppercase tracking-widest bg-neutral-200 text-neutral-700 px-2 py-0.5 rounded-full font-bold">Hidden</span>;
}

function IconBtn({ children, title, onClick, disabled, testid }) {
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled}
            data-testid={testid}
            className="w-6 h-6 inline-flex items-center justify-center border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed">
      {children}
    </button>
  );
}

function EditItemDialog({ kind, item, releaseContext, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    code: item.code || "",
    name: item.name || "",
    public_display_name: item.public_display_name || item.name || "",
    slug: item.slug || "",
    description: item.description || "",
    display_order: item.display_order ?? 0,
    active: item.active !== false,
    published: item.published !== false,
    country_or_region: item.country_or_region || "",
    item_type: item.item_type || "album_variant",
    completion_tracking_enabled: item.completion_tracking_enabled !== false,
    base_sticker_total: item.base_sticker_total ?? 0,
    total_required: item.total_required ?? "",
  });
  const [busy, setBusy] = useState(false);
  const label = { family: "Collection", release: "Release", variant: "Catalog Item" }[kind] || "Item";
  const path = {
    family: "collection-families", release: "releases", variant: "album-variants",
  }[kind];
  async function save() {
    setBusy(true);
    try {
      if (isNew) {
        const payload = {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          public_display_name: form.public_display_name.trim() || form.name.trim(),
          slug: (form.slug || "").trim() || undefined,
          description: form.description || undefined,
          display_order: Number(form.display_order) || 0,
          active: form.active,
          published: form.published,
          country_or_region: form.country_or_region || undefined,
          item_type: form.item_type,
          completion_tracking_enabled: form.completion_tracking_enabled,
          base_sticker_total: Number(form.base_sticker_total) || 0,
          total_required: form.total_required === "" ? undefined : Number(form.total_required),
          release_id: item.release_id,
        };
        await api.post(`/admin/catalog/${path}`, payload);
        toast.success(`${label} created.`);
      } else {
        const payload = {
          name: form.name.trim() || undefined,
          public_display_name: form.public_display_name.trim(),
          description: form.description,
          display_order: Number(form.display_order) || 0,
          active: form.active,
          published: form.published,
        };
        if (kind === "variant") {
          payload.item_type = form.item_type;
          payload.completion_tracking_enabled = form.completion_tracking_enabled;
          payload.country_or_region = form.country_or_region || null;
        }
        await api.patch(`/admin/catalog/${path}/${item.id}`, payload);
        toast.success(`${label} updated.`);
      }
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e) || "Save failed.");
    } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="catalog-edit-dialog">
      <div className="bg-white rounded-2xl border-2 border-[var(--ink)] max-w-xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">{isNew ? `New ${label}` : `Edit ${label}`}</h3>
          <button onClick={onClose} className="text-[var(--smoke)]"><X size={18}/></button>
        </div>
        {releaseContext && (
          <div className="text-xs text-[var(--smoke)] mb-3">
            Inside release: <span className="font-mono">{releaseContext.code}</span>
            {releaseContext.public_display_name ? ` · ${releaseContext.public_display_name}` : ""}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Public Display Name *" value={form.public_display_name}
                 onChange={(v) => setForm({...form, public_display_name: v})}
                 testid="catalog-field-pdn" />
          <Field label={isNew ? "Internal Code * (unique)" : "Internal Code (read-only)"}
                 value={isNew ? form.code : item.code || ""}
                 onChange={isNew ? (v) => setForm({...form, code: v}) : () => {}}
                 testid="catalog-field-code" />
          <Field label="Short / Internal Name" value={form.name}
                 onChange={(v) => setForm({...form, name: v})}
                 testid="catalog-field-name" />
          <Field label="Display Order (lower = earlier)" type="number"
                 value={form.display_order}
                 onChange={(v) => setForm({...form, display_order: v})}
                 testid="catalog-field-order" />
          {kind === "variant" && (
            <>
              <SelectField label="Item Type" value={form.item_type}
                           onChange={(v) => setForm({...form, item_type: v})}
                           options={ITEM_TYPES.map((t) => ({value: t.value, label: t.label}))}
                           testid="catalog-field-item-type" />
              <Field label="Region / Country" value={form.country_or_region}
                     onChange={(v) => setForm({...form, country_or_region: v})}
                     testid="catalog-field-region" />
              <Field label="Sticker Count / Total Items" type="number"
                     value={form.total_required} onChange={(v) => setForm({...form, total_required: v})}
                     testid="catalog-field-total" />
              <label className="col-span-1 sm:col-span-2 inline-flex items-center gap-2 mt-2 text-sm"
                     data-testid="catalog-field-completion-wrap">
                <input type="checkbox" checked={form.completion_tracking_enabled}
                       onChange={(e) => setForm({...form, completion_tracking_enabled: e.target.checked})}
                       data-testid="catalog-field-completion" />
                Completion tracking enabled (show % complete). Turn OFF for boxes,
                special stickers, or premium collectibles where a percentage doesn&apos;t apply.
              </label>
            </>
          )}
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
                   onChange={(e) => setForm({...form, active: e.target.checked})}
                   data-testid="catalog-field-active" />
            Active (available in admin)
          </label>
          {kind !== "family" && (
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published}
                     onChange={(e) => setForm({...form, published: e.target.checked})}
                     data-testid="catalog-field-published" />
              Published (visible to collectors)
            </label>
          )}
          <label className="col-span-1 sm:col-span-2 block">
            <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Description</span>
            <textarea rows={3} value={form.description}
                      onChange={(e) => setForm({...form, description: e.target.value})}
                      className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
                      data-testid="catalog-field-description" />
          </label>
        </div>
        <div className="mt-5 flex items-center gap-3 justify-end">
          <button onClick={onClose} className="border-2 border-[var(--ink)] rounded-lg px-4 py-2 text-sm">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
                  className="bg-[var(--panini-red)] text-white rounded-lg px-6 py-2 font-bold disabled:opacity-40"
                  data-testid="catalog-save-btn">
            {busy ? "Saving…" : (isNew ? "Create" : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Categories tab
// ═══════════════════════════════════════════════════════════════════
function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", description: "", display_order: 100 });
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/catalog/sticker-categories");
      setItems(r.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setMsg("");
    try {
      await api.post("/admin/catalog/sticker-categories", {
        ...form,
        code: form.code.toUpperCase().trim(),
        display_order: Number(form.display_order) || 0,
      });
      setForm({ code: "", name: "", description: "", display_order: 100 });
      setCreating(false);
      await load();
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to create.");
    }
  };

  const del = async (id, code) => {
    if (!window.confirm(`Delete category ${code}?  This cannot be undone.`)) return;
    setMsg("");
    try {
      await api.delete(`/admin/catalog/sticker-categories/${id}`);
      await load();
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to delete.");
    }
  };

  const toggleActive = async (row) => {
    if (!row.active) {
      await api.patch(`/admin/catalog/sticker-categories/${row.id}`, { active: true });
    } else {
      await api.post(`/admin/catalog/sticker-categories/${row.id}/deactivate`);
    }
    await load();
  };

  if (loading) return <SpinBox testid="cats-loading" />;

  return (
    <div className="space-y-4" data-testid="categories-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[var(--smoke)]">
          {items.length} categories — administrator-managed, extensible taxonomy.
        </div>
        <button data-testid="cat-add-btn"
                onClick={() => setCreating(true)}
                className="btn-primary flex items-center gap-2">
          <Plus size={14} /> Add category
        </button>
      </div>

      {msg && <Banner tone="error">{msg}</Banner>}

      {creating && (
        <div className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 space-y-3"
             data-testid="cat-create-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Code (UPPER_CASE)"
                   value={form.code}
                   onChange={v => setForm({...form, code: v.toUpperCase()})}
                   testid="cat-form-code" />
            <Field label="Name" value={form.name}
                   onChange={v => setForm({...form, name: v})} testid="cat-form-name" />
            <Field label="Display order" type="number" value={form.display_order}
                   onChange={v => setForm({...form, display_order: v})}
                   testid="cat-form-order" />
            <Field label="Description" value={form.description}
                   onChange={v => setForm({...form, description: v})}
                   testid="cat-form-desc" />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={create} data-testid="cat-form-save">Save</button>
            <button className="btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      <table className="w-full text-sm border-2 border-[var(--ink)] bg-white rounded-2xl overflow-hidden"
             data-testid="cats-table">
        <thead className="text-[10px] uppercase tracking-widest bg-neutral-100">
          <tr>
            <th className="text-left px-3 py-2">Code</th>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-right px-3 py-2">Order</th>
            <th className="text-center px-3 py-2">Active</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} className="border-t border-neutral-200" data-testid={`cat-row-${c.code}`}>
              <td className="px-3 py-2 font-mono font-bold">{c.code}</td>
              <td className="px-3 py-2">{c.name}</td>
              <td className="px-3 py-2 text-[var(--smoke)]">{c.description || "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{c.display_order}</td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => toggleActive(c)} className="text-xs underline"
                        data-testid={`cat-toggle-${c.code}`}>
                  {c.active ? "Active" : "Inactive"}
                </button>
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => del(c.id, c.code)}
                        className="text-red-700 hover:text-red-900"
                        data-testid={`cat-delete-${c.code}`}
                        title="Delete category">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Stickers tab
// ═══════════════════════════════════════════════════════════════════
function StickersTab() {
  const [releases, setReleases] = useState([]);
  const [cats, setCats] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [stickers, setStickers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    release_id: "", sticker_number: "", name: "", category_id: "",
    team_code: "", country_code: "",
    player_first_name: "", player_last_name: "",
  });
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const [rr, cc] = await Promise.all([
        api.get("/admin/catalog/releases"),
        api.get("/admin/catalog/sticker-categories"),
      ]);
      setReleases(rr.data.items);
      setCats(cc.data.items.filter(x => x.active));
      if (rr.data.items[0]) setReleaseId(rr.data.items[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!releaseId) return;
    setLoading(true);
    api.get("/admin/catalog/stickers",
            { params: { release_id: releaseId, q: q || undefined, limit: 500 } })
      .then(r => setStickers(r.data.items))
      .finally(() => setLoading(false));
  }, [releaseId, q]);

  const openCreate = () => {
    setCreating(true);
    setForm(f => ({...f, release_id: releaseId,
                        category_id: cats.find(c=>c.code==="REGULAR")?.id || cats[0]?.id || ""}));
    setMsg("");
  };

  const save = async () => {
    setMsg("");
    try {
      await api.post("/admin/catalog/stickers", form);
      setCreating(false);
      // reload
      const r = await api.get("/admin/catalog/stickers",
                              { params: { release_id: releaseId, limit: 500 } });
      setStickers(r.data.items);
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to create sticker.");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete sticker ${s.sticker_number}?`)) return;
    setMsg("");
    try {
      await api.delete(`/admin/catalog/stickers/${s.id}`);
      setStickers(x => x.filter(y => y.id !== s.id));
    } catch (e) {
      setMsg(fmtErr(e) || "Cannot delete.");
    }
  };

  return (
    <div className="space-y-4" data-testid="stickers-tab">
      <div className="flex flex-wrap items-end gap-2">
        <SelectField label="Release" value={releaseId} onChange={setReleaseId}
                     options={releases.map(r => [r.id, `${r.code} · ${r.name}`])}
                     testid="stickers-release" />
        <Field label="Search" value={q} onChange={setQ} testid="stickers-search" />
        <button className="btn-primary flex items-center gap-1" onClick={openCreate}
                disabled={!releaseId}
                data-testid="sticker-add-btn">
          <Plus size={14} /> Add sticker
        </button>
      </div>

      {msg && <Banner tone="error">{msg}</Banner>}
      {creating && (
        <div className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 space-y-3"
             data-testid="sticker-create-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Number" value={form.sticker_number}
                   onChange={v => setForm({...form, sticker_number: v})}
                   testid="stk-form-number" />
            <Field label="Name" value={form.name}
                   onChange={v => setForm({...form, name: v})}
                   testid="stk-form-name" />
            <SelectField label="Category" value={form.category_id}
                         onChange={v => setForm({...form, category_id: v})}
                         options={cats.map(c => [c.id, `${c.code} · ${c.name}`])}
                         testid="stk-form-category" />
            <Field label="Team code (opt)" value={form.team_code}
                   onChange={v => setForm({...form, team_code: v})} />
            <Field label="Country (opt)" value={form.country_code}
                   onChange={v => setForm({...form, country_code: v})} />
            <Field label="Player first name" value={form.player_first_name}
                   onChange={v => setForm({...form, player_first_name: v})} />
            <Field label="Player last name" value={form.player_last_name}
                   onChange={v => setForm({...form, player_last_name: v})} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={save}
                    data-testid="stk-form-save">Save</button>
            <button className="btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <SpinBox testid="stk-loading" /> : (
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
          <div className="px-3 py-2 text-xs text-[var(--smoke)] bg-neutral-100"
               data-testid="stk-count">
            {stickers.length} sticker{stickers.length === 1 ? "" : "s"} in this release
          </div>
          <table className="w-full text-sm" data-testid="stk-table">
            <thead className="text-[10px] uppercase tracking-widest bg-neutral-50">
              <tr>
                <th className="text-left px-3 py-1">#</th>
                <th className="text-left px-3 py-1">Name</th>
                <th className="text-left px-3 py-1">Category</th>
                <th className="text-left px-3 py-1">Team</th>
                <th className="text-left px-3 py-1">Player</th>
                <th className="text-right px-3 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stickers.map(s => {
                const catRow = cats.find(c => c.id === s.category_id);
                return (
                  <tr key={s.id} className="border-t border-neutral-200"
                      data-testid={`stk-row-${s.sticker_number}`}>
                    <td className="px-3 py-1 font-mono font-bold">{s.sticker_number}</td>
                    <td className="px-3 py-1">{s.name}</td>
                    <td className="px-3 py-1 text-xs font-mono">
                      {catRow?.code || s.category_id}
                    </td>
                    <td className="px-3 py-1">{s.team_code || "—"}</td>
                    <td className="px-3 py-1">
                      {[s.player_first_name, s.player_last_name].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-3 py-1 text-right">
                      <button onClick={() => del(s)} className="text-red-700 hover:text-red-900"
                              data-testid={`stk-delete-${s.sticker_number}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Checklist Groups tab
// ═══════════════════════════════════════════════════════════════════
function ChecklistGroupsTab() {
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    release_id: "", code: "", name: "", description: "",
    group_type: "base", display_order: 100,
  });
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/catalog/releases").then(r => {
      setReleases(r.data.items);
      if (r.data.items[0]) setReleaseId(r.data.items[0].id);
    });
  }, []);
  const load = async (rid) => {
    if (!rid) return;
    setLoading(true);
    try {
      const r = await api.get("/admin/catalog/checklist-groups", { params: { release_id: rid } });
      setGroups(r.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(releaseId); }, [releaseId]);

  const openCreate = () => {
    setCreating(true);
    setForm({ release_id: releaseId, code: "", name: "", description: "",
              group_type: "base", display_order: 100 });
    setMsg("");
  };
  const save = async () => {
    setMsg("");
    try {
      await api.post("/admin/catalog/checklist-groups", {
        ...form, code: form.code.toUpperCase().trim(),
        display_order: Number(form.display_order) || 0,
      });
      setCreating(false);
      await load(releaseId);
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to create.");
    }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete checklist group ${g.code}?  All memberships will be removed.`)) return;
    setMsg("");
    try {
      await api.delete(`/admin/catalog/checklist-groups/${g.id}`);
      await load(releaseId);
    } catch (e) {
      setMsg(fmtErr(e) || "Cannot delete.");
    }
  };

  return (
    <div className="space-y-4" data-testid="groups-tab">
      <div className="flex items-end gap-2">
        <SelectField label="Release" value={releaseId} onChange={setReleaseId}
                     options={releases.map(r => [r.id, `${r.code} · ${r.name}`])}
                     testid="groups-release" />
        <button className="btn-primary flex items-center gap-1" onClick={openCreate}
                disabled={!releaseId} data-testid="group-add-btn">
          <Plus size={14} /> Add checklist group
        </button>
      </div>

      {msg && <Banner tone="error">{msg}</Banner>}
      {creating && (
        <div className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 space-y-3"
             data-testid="group-create-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Code (UPPER_CASE)" value={form.code}
                   onChange={v => setForm({...form, code: v.toUpperCase()})}
                   testid="grp-form-code" />
            <Field label="Name" value={form.name}
                   onChange={v => setForm({...form, name: v})}
                   testid="grp-form-name" />
            <SelectField label="Type" value={form.group_type}
                         onChange={v => setForm({...form, group_type: v})}
                         options={[["base","Base"],["promotional","Promotional"],
                                   ["regional","Regional"],["special","Special"]]}
                         testid="grp-form-type" />
            <Field label="Display order" type="number" value={form.display_order}
                   onChange={v => setForm({...form, display_order: v})} />
            <Field label="Description" value={form.description}
                   onChange={v => setForm({...form, description: v})} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={save} data-testid="grp-form-save">Save</button>
            <button className="btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <SpinBox testid="grp-loading" /> : (
        <table className="w-full text-sm border-2 border-[var(--ink)] bg-white rounded-2xl overflow-hidden"
               data-testid="grp-table">
          <thead className="text-[10px] uppercase tracking-widest bg-neutral-100">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Stickers</th>
              <th className="text-center px-3 py-2">Active</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.id} className="border-t border-neutral-200"
                  data-testid={`grp-row-${g.code}`}>
                <td className="px-3 py-2 font-mono font-bold">{g.code}</td>
                <td className="px-3 py-2">{g.name}</td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest">{g.group_type}</td>
                <td className="px-3 py-2 text-right font-mono"
                    data-testid={`grp-count-${g.code}`}>{g.sticker_count}</td>
                <td className="px-3 py-2 text-center">{g.active ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => del(g)} className="text-red-700 hover:text-red-900"
                          data-testid={`grp-delete-${g.code}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Variant ↔ Checklist Groups tab
// ═══════════════════════════════════════════════════════════════════
function VariantChecklistsTab() {
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [variants, setVariants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [variantId, setVariantId] = useState("");
  const [links, setLinks] = useState([]);
  const [chosenGroup, setChosenGroup] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/catalog/releases").then(r => {
      setReleases(r.data.items);
      if (r.data.items[0]) setReleaseId(r.data.items[0].id);
    });
  }, []);

  useEffect(() => {
    if (!releaseId) return;
    Promise.all([
      api.get("/admin/catalog/album-variants", { params: { release_id: releaseId } }),
      api.get("/admin/catalog/checklist-groups", { params: { release_id: releaseId } }),
    ]).then(([v, g]) => {
      setVariants(v.data.items);
      setGroups(g.data.items);
      setVariantId(v.data.items[0]?.id || "");
    });
  }, [releaseId]);

  const loadLinks = async (vid) => {
    if (!vid) { setLinks([]); return; }
    const r = await api.get(`/admin/catalog/album-variants/${vid}/checklists`);
    setLinks(r.data.items);
  };
  useEffect(() => { loadLinks(variantId); }, [variantId]);

  const attach = async () => {
    if (!chosenGroup) return;
    setMsg("");
    try {
      await api.post(`/admin/catalog/album-variants/${variantId}/checklists`,
                     { checklist_group_id: chosenGroup });
      await loadLinks(variantId);
      setChosenGroup("");
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to attach.");
    }
  };
  const detach = async (gid) => {
    setMsg("");
    try {
      await api.delete(`/admin/catalog/album-variants/${variantId}/checklists/${gid}`);
      await loadLinks(variantId);
    } catch (e) {
      setMsg(fmtErr(e) || "Failed to detach.");
    }
  };

  return (
    <div className="space-y-4" data-testid="variants-tab">
      <div className="flex flex-wrap items-end gap-2">
        <SelectField label="Release" value={releaseId} onChange={setReleaseId}
                     options={releases.map(r => [r.id, `${r.code} · ${r.name}`])}
                     testid="var-release" />
        <SelectField label="Album variant" value={variantId} onChange={setVariantId}
                     options={variants.map(v => [v.id, `${v.code} · ${v.name}`])}
                     testid="var-variant" />
      </div>

      {msg && <Banner tone="error">{msg}</Banner>}

      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4 space-y-3">
        <div className="flex items-end gap-2">
          <SelectField label="Attach checklist group" value={chosenGroup}
                       onChange={setChosenGroup}
                       options={[["", "— select —"],
                                  ...groups.filter(g => !links.some(l => l.link.checklist_group_id === g.id))
                                          .map(g => [g.id, `${g.code} · ${g.name}`])]}
                       testid="var-choose-group" />
          <button className="btn-primary" onClick={attach}
                  disabled={!chosenGroup || !variantId}
                  data-testid="var-attach">Attach</button>
        </div>

        <table className="w-full text-sm" data-testid="var-links">
          <thead className="text-[10px] uppercase tracking-widest bg-neutral-100">
            <tr>
              <th className="text-left px-3 py-2">Group code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.map(l => (
              <tr key={l.link.id} className="border-t border-neutral-200"
                  data-testid={`var-link-${l.checklist_group?.code}`}>
                <td className="px-3 py-2 font-mono">{l.checklist_group?.code}</td>
                <td className="px-3 py-2">{l.checklist_group?.name}</td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest">
                  {l.checklist_group?.group_type}
                </td>
                <td className="px-3 py-2 text-right">
                  <button className="text-red-700"
                          data-testid={`var-detach-${l.checklist_group?.code}`}
                          onClick={() => detach(l.checklist_group.id)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {links.length === 0 && (
              <tr><td colSpan="4" className="px-3 py-6 text-center text-[var(--smoke)]"
                       data-testid="var-empty">
                No checklist groups attached to this variant.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Iter Catalog-Cover-Meta — official cover for the selected variant. */}
      {variantId && (
        <CatalogCoverPanel kind="variant" targetId={variantId}
                            key={`var-cover-${variantId}`} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Iter QA #2 — Cover Images tab (single pane for bulk cover uploads)
// ═══════════════════════════════════════════════════════════════════
function CoverImagesTab() {
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [variants, setVariants] = useState([]);
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const bump = () => setReloadTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/catalog/releases");
        if (cancelled) return;
        setReleases(r.data.items || []);
        if (!releaseId && r.data.items?.[0]) setReleaseId(r.data.items[0].id);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!releaseId) { setVariants([]); setRelease(null); return; }
    let cancelled = false;
    (async () => {
      const [v, rels] = await Promise.all([
        api.get("/admin/catalog/album-variants", { params: { release_id: releaseId } }),
        api.get("/admin/catalog/releases"),
      ]);
      if (cancelled) return;
      setVariants(v.data.items || []);
      const rel = (rels.data.items || []).find((x) => x.id === releaseId);
      setRelease(rel || null);
    })();
    return () => { cancelled = true; };
  }, [releaseId, reloadTick]);

  return (
    <div className="space-y-6" data-testid="cover-images-tab">
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)] p-4">
        <div className="text-xs uppercase tracking-widest font-bold mb-1">Cover images</div>
        <p className="text-sm text-[var(--smoke)]">
          Upload official artwork for each album variant. A 600 px WebP thumbnail
          is auto-generated for card and grid views; the original is preserved
          for full-size displays. Priority: <b>collector custom cover → variant cover
          → release cover → CollectorUp placeholder</b>. Variant covers override the
          release cover for those variants.
        </p>
      </div>

      <label className="block max-w-md">
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">Release</div>
        <select className="gm-field w-full" value={releaseId}
                data-testid="covers-release-picker"
                onChange={(e) => setReleaseId(e.target.value)}>
          {(releases || []).map((r) => (
            <option key={r.id} value={r.id}>{r.code} · {r.name}</option>
          ))}
        </select>
      </label>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--smoke)]">
          <Loader2 size={14} className="animate-spin" /> Loading catalog…
        </div>
      ) : (
        <>
          {release && (
            <div>
              <div className="text-xs uppercase tracking-widest font-bold mb-2">
                Release cover — used as fallback when a variant has no cover
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <CoverCard kind="release" target={release} onChange={bump} />
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-widest font-bold mb-2">
              Variant covers ({variants.length})
            </div>
            {variants.length === 0 ? (
              <div className="border-2 border-dashed border-[var(--line)] rounded-2xl p-6 text-sm text-[var(--smoke)]">
                No album variants exist for this release yet. Create them under
                the <b>Hierarchy</b> tab first.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {variants.map((v) => (
                  <CoverCard key={v.id} kind="variant" target={v} onChange={bump} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CoverCard({ kind, target, onChange }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const cover = target.cover_image;
  const url = target.cover_image_url;
  const endpoint = kind === "variant"
    ? `/admin/catalog/album-variants/${target.id}`
    : `/admin/catalog/releases/${target.id}`;

  const onFile = async (f) => {
    if (!f) return;
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`${endpoint}/cover/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.();
    } catch (e) {
      setMsg(fmtErr(e) || "Upload failed.");
    } finally { setBusy(false); }
  };

  const onClear = async () => {
    if (!window.confirm(`Remove the ${kind} cover for ${target.name}?`)) return;
    setBusy(true); setMsg("");
    try { await api.delete(`${endpoint}/cover`); onChange?.(); }
    catch (e) { setMsg(fmtErr(e) || "Clear failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4 space-y-3"
         data-testid={`cover-card-${kind}-${target.code || target.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-xs font-bold">{target.code}</div>
          <div className="font-display text-base leading-tight">{target.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mt-1">
            {kind}
            {target.country_code ? ` · ${target.country_code}` : ""}
            {target.format ? ` · ${target.format}` : ""}
          </div>
        </div>
        {cover ? (
          <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-900 font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-green-300"
                 data-testid="cover-status-assigned">
            <span className="w-2 h-2 rounded-full bg-green-600" aria-hidden="true"></span>
            Cover assigned
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] bg-yellow-100 text-yellow-900 font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-yellow-400"
                 data-testid="cover-status-missing">
            <span className="w-2 h-2 rounded-full bg-yellow-500" aria-hidden="true"></span>
            Artwork missing
          </span>
        )}
      </div>

      {/* Iter QA #005 — contain-fit on neutral bg so uploaded artwork of any
          aspect ratio previews uniformly across the grid, never cropped. */}
      <div className="border-2 border-[var(--ink)] rounded-xl bg-neutral-50 aspect-square overflow-hidden flex items-center justify-center">
        {url ? (
          <img src={url} alt=""
               className="max-w-full max-h-full w-auto h-auto object-contain object-center"
               data-testid="cover-card-img" />
        ) : (
          <span className="text-[11px] text-[var(--smoke)] px-3 text-center">
            No cover set
          </span>
        )}
      </div>

      {cover && (
        <dl className="text-[11px] grid grid-cols-[80px_1fr] gap-y-0.5 gap-x-2">
          <dt className="text-[var(--smoke)]">Dimensions</dt>
          <dd className="font-medium">
            {cover.width && cover.height ? `${cover.width} × ${cover.height}` : "—"}
          </dd>
          <dt className="text-[var(--smoke)]">Size</dt>
          <dd className="font-medium">
            {cover.file_size ? `${Math.round(cover.file_size / 1024)} KB` : "—"}
          </dd>
          <dt className="text-[var(--smoke)]">Type</dt>
          <dd className="font-medium">{cover.mime_type || "—"}</dd>
          <dt className="text-[var(--smoke)]">By</dt>
          <dd className="font-medium truncate" title={cover.uploaded_by || ""}>
            {cover.uploaded_by || "—"}
          </dd>
        </dl>
      )}

      {msg && <div className="text-xs text-red-700">{msg}</div>}

      <div className="flex flex-wrap gap-2">
        <label className={"btn-primary text-xs cursor-pointer" + (busy ? " opacity-50 pointer-events-none" : "")}
               data-testid={`cover-card-upload-${target.code || target.id}`}>
          {cover ? "Replace" : "Upload"}
          <input type="file" className="hidden"
                 accept="image/jpeg,image/png,image/webp"
                 onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
        {cover && (
          <button className="btn-secondary text-xs" disabled={busy}
                  onClick={onClear}
                  data-testid={`cover-card-clear-${target.code || target.id}`}>
            Remove
          </button>
        )}
        {url && (
          <a href={url} target="_blank" rel="noreferrer"
             className="btn-ghost text-xs">
            View
          </a>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Iter Catalog-Cover-Meta — Catalog Cover Panel
// ═══════════════════════════════════════════════════════════════════
function CatalogCoverPanel({ kind, targetId }) {
  const [state, setState] = useState({ loading: true, cover: null, meta: null });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const endpoint = kind === "variant"
    ? `/admin/catalog/album-variants/${targetId}`
    : `/admin/catalog/releases/${targetId}`;
  const listEndpoint = kind === "variant"
    ? "/admin/catalog/album-variants"
    : "/admin/catalog/releases";

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    setMsg("");
    try {
      const r = await api.get(listEndpoint,
        kind === "variant" ? {} : {});
      const items = r.data?.items || [];
      const row = items.find((x) => x.id === targetId);
      const cover = row?.cover_image || null;
      setState({ loading: false, cover,
                  url: row?.cover_image_url || null });
    } catch (e) {
      setState({ loading: false, cover: null, url: null });
      setMsg(fmtErr(e) || "Could not load cover metadata.");
    }
  };
  useEffect(() => { load(); }, [targetId]);

  const onFile = async (f) => {
    if (!f) return;
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`${endpoint}/cover/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) {
      setMsg(fmtErr(e) || "Upload failed.");
    } finally { setBusy(false); }
  };
  const onClear = async () => {
    if (!window.confirm("Remove the current catalog cover?")) return;
    setBusy(true); setMsg("");
    try {
      await api.delete(`${endpoint}/cover`);
      await load();
    } catch (e) {
      setMsg(fmtErr(e) || "Clear failed.");
    } finally { setBusy(false); }
  };

  const cover = state.cover;
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4 space-y-3"
         data-testid={`catalog-cover-${kind}`}>
      <div className="flex items-baseline justify-between">
        <div className="font-display text-lg">Official catalog cover</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
          {kind === "variant" ? "Variant" : "Release"} · WebP 600px thumbnail auto-generated
        </div>
      </div>

      {msg && <Banner tone="error">{msg}</Banner>}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
        {/* Iter QA #005 — contain-fit on neutral bg for the panel preview. */}
        <div className="border-2 border-[var(--ink)] rounded-xl bg-neutral-50 aspect-square overflow-hidden flex items-center justify-center"
             data-testid="catalog-cover-preview">
          {state.loading
            ? <span className="text-xs text-[var(--smoke)]">Loading…</span>
            : cover?.thumbnail_url
              ? <img src={cover.thumbnail_url} alt=""
                     className="max-w-full max-h-full w-auto h-auto object-contain object-center"
                     data-testid="catalog-cover-preview-img" />
              : <span className="text-xs text-[var(--smoke)] px-4 text-center">
                  No cover set. The release cover (if any) will be used instead.
                </span>}
        </div>
        <div className="space-y-2 text-sm">
          {cover ? (
            <dl className="grid grid-cols-[110px_1fr] gap-y-1 gap-x-3 text-xs">
              <MetaRow k="Dimensions"
                v={cover.width && cover.height ? `${cover.width} × ${cover.height} px` : "—"} />
              <MetaRow k="File size"
                v={cover.file_size ? `${Math.round(cover.file_size / 1024)} KB` : "—"} />
              <MetaRow k="Type" v={cover.mime_type || "—"} />
              <MetaRow k="Uploaded" v={fmtDate(cover.uploaded_at)} />
              <MetaRow k="By" v={cover.uploaded_by || "—"} />
              <MetaRow k="Original" v={cover.original_url
                ? <a href={cover.original_url} target="_blank" rel="noreferrer"
                      className="text-[var(--panini-red)] underline break-all">
                    Open full-size
                  </a> : "—"} />
            </dl>
          ) : (
            <p className="text-[var(--smoke)] text-xs">
              Upload a JPEG, PNG, or WebP image. A 600px WebP thumbnail is
              generated automatically and served alongside the original.
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <label className={"btn-primary cursor-pointer" + (busy ? " opacity-50 pointer-events-none" : "")}
                   data-testid="catalog-cover-upload">
              {cover ? "Replace cover" : "Upload cover"}
              <input type="file" className="hidden"
                     accept="image/jpeg,image/png,image/webp"
                     onChange={(e) => onFile(e.target.files?.[0])} />
            </label>
            {cover && (
              <button className="btn-secondary text-xs" disabled={busy}
                      onClick={onClear} data-testid="catalog-cover-clear">
                Remove cover
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{k}</dt>
      <dd className="font-medium break-words">{v ?? "—"}</dd>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Importer tab — Upload → Dry-run preview → Confirm → Results / Rollback
// ═══════════════════════════════════════════════════════════════════
function ImporterTab() {
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [batch, setBatch] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/catalog/releases").then(r => {
      setReleases(r.data.items);
      if (r.data.items[0]) setReleaseId(r.data.items[0].id);
    });
  }, []);
  const loadBatches = async () => {
    const r = await api.get("/admin/catalog/import/batches");
    setBatches(r.data.items);
  };
  useEffect(() => { loadBatches(); }, []);

  const doDryRun = async () => {
    if (!file || !releaseId) return;
    setBusy(true); setMsg(""); setReport(null); setBatch(null); setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("release_id", releaseId);
      // Let the browser set the multipart boundary — override the shared
      // axios default which is `application/json`.
      const r = await api.post("/admin/catalog/import/dry-run", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data.report);
      setBatch(r.data.batch);
      await loadBatches();
    } catch (e) {
      setMsg(fmtErr(e) || "Dry-run failed.");
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!batch) return;
    if (!window.confirm(
        `Commit ${report.valid_rows} rows to the catalog?\n\n` +
        `This will insert stickers + checklist memberships. You can rollback afterwards from the Batch History.`
    )) return;
    setCommitting(true); setMsg("");
    try {
      const r = await api.post(`/admin/catalog/import/${batch.id}/commit`);
      setCommitResult(r.data.result);
      setBatch(r.data.batch);
      await loadBatches();
    } catch (e) {
      setMsg(fmtErr(e) || "Commit failed.");
    } finally { setCommitting(false); }
  };

  const doRollback = async (b) => {
    if (!window.confirm(
        `Rollback batch ${b.source_filename}?\n\n` +
        `This removes stickers and checklist memberships that this batch created. ` +
        `Records referenced elsewhere are preserved.`
    )) return;
    setMsg("");
    try {
      const r = await api.post(`/admin/catalog/import/${b.id}/rollback`);
      alert(
        `Rolled back.\n` +
        `Removed stickers: ${r.data.result.removed_stickers}\n` +
        `Removed memberships: ${r.data.result.removed_memberships}\n` +
        `Preserved (still referenced): ${r.data.result.skipped_stickers?.length || 0}`
      );
      await loadBatches();
    } catch (e) {
      setMsg(fmtErr(e) || "Rollback failed.");
    }
  };

  const reset = () => { setFile(null); setReport(null); setBatch(null); setCommitResult(null); setMsg(""); };

  const releaseLabel = (rid) => {
    const r = releases.find(x => x.id === rid);
    return r ? `${r.code} · ${r.name}` : rid;
  };

  return (
    <div className="space-y-6" data-testid="importer-tab">
      {/* ── Step 1 — Upload ── */}
      <section className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
        <header className="px-5 py-3 bg-[var(--panini-yellow-soft)] flex items-center gap-2">
          <Upload size={16} />
          <div className="font-display text-lg">1 · Upload</div>
        </header>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField label="Target release" value={releaseId} onChange={setReleaseId}
                       options={releases.map(r => [r.id, `${r.code} · ${r.name}`])}
                       testid="imp-release" />
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
              File (.csv / .xlsx / .json)
            </span>
            <input type="file" accept=".csv,.xlsx,.json"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   data-testid="imp-file" />
          </label>
          <div className="flex items-end gap-2">
            <button className="btn-primary flex items-center gap-1"
                    disabled={!file || !releaseId || busy}
                    onClick={doDryRun}
                    data-testid="imp-dryrun-btn">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <ListChecks size={14} />}
              Dry-run
            </button>
            {(report || batch) && (
              <button className="btn-secondary" onClick={reset} data-testid="imp-reset">
                <X size={14} /> Reset
              </button>
            )}
          </div>
        </div>
        <div className="px-5 pb-4 text-xs text-[var(--smoke)]">
          Required columns: <code className="font-mono">sticker_number, name, category_code</code>.
          Optional: <code className="font-mono">team_code, country_code, display_order,
          player_first_name, player_last_name, player_position, player_jersey_number,
          checklist_group_code, checklist_sequence, checklist_required</code>.
        </div>
      </section>

      {msg && <Banner tone="error">{msg}</Banner>}

      {/* ── Step 2 — Dry-run preview ── */}
      {report && (
        <section className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden"
                 data-testid="imp-preview">
          <header className="px-5 py-3 bg-neutral-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ListChecks size={16} />
              <div className="font-display text-lg">2 · Dry-run preview</div>
              <span className="text-xs font-mono text-[var(--smoke)]">
                {batch?.source_filename} · {report.format.toUpperCase()}
              </span>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-900"
                    data-testid="imp-valid-count">
                {report.valid_rows} valid
              </span>
              <span className={"px-2 py-1 rounded " +
                (report.error_rows === 0 ? "bg-neutral-100 text-neutral-700" : "bg-red-100 text-red-900")}
                    data-testid="imp-error-count">
                {report.error_rows} errors
              </span>
              <span className="px-2 py-1 rounded bg-neutral-100 text-neutral-700"
                    data-testid="imp-total-count">
                {report.total_rows} total
              </span>
            </div>
          </header>

          {report.errors?.length > 0 && (
            <div className="px-5 py-3 border-b border-neutral-200 bg-red-50" data-testid="imp-errors">
              <div className="text-xs uppercase font-bold text-red-900 tracking-widest mb-2">
                Row-level errors
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 text-xs font-mono">
                {report.errors.map((e, i) => (
                  <div key={i} className="text-red-900" data-testid="imp-error-row">
                    Row {e.row} · {e.field} · <span className="font-bold">{e.code}</span> — {e.message}
                  </div>
                ))}
                {report.errors_truncated &&
                  <div className="text-red-700 italic">…errors list truncated (200 max).</div>}
              </div>
            </div>
          )}

          {report.preview?.length > 0 && (
            <div className="px-5 py-3">
              <div className="text-xs uppercase font-bold text-[var(--smoke)] tracking-widest mb-2">
                Preview (first {report.preview.length} valid rows)
              </div>
              <table className="w-full text-xs" data-testid="imp-preview-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest bg-neutral-50">
                    <th className="text-left px-2 py-1">Row #</th>
                    <th className="text-left px-2 py-1">Number</th>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Category</th>
                    <th className="text-left px-2 py-1">Checklist</th>
                  </tr>
                </thead>
                <tbody>
                  {report.preview.map(row => (
                    <tr key={row.row_number} className="border-t">
                      <td className="px-2 py-1">{row.row_number}</td>
                      <td className="px-2 py-1 font-mono">{row.data.sticker_number}</td>
                      <td className="px-2 py-1">{row.data.name}</td>
                      <td className="px-2 py-1 font-mono">{row.data.category_code}</td>
                      <td className="px-2 py-1 font-mono">{row.data.checklist_group_code || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Step 3 — Confirm */}
          <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50"
               data-testid="imp-confirm-band">
            {report.error_rows > 0 ? (
              <Banner tone="error">
                Fix errors above and re-upload before committing.
              </Banner>
            ) : commitResult ? (
              <Banner tone="success">
                <CheckCircle2 size={14} className="inline mr-1" />
                Committed to <b>{releaseLabel(releaseId)}</b> — {commitResult.created_stickers} stickers,
                {" "}{commitResult.created_memberships} checklist memberships.
              </Banner>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  Ready to insert <b>{report.valid_rows}</b> stickers into <b>{releaseLabel(releaseId)}</b>.
                  Rollback is available from the Batch History section below.
                </div>
                <button className="btn-primary flex items-center gap-1"
                        onClick={doCommit} disabled={committing}
                        data-testid="imp-confirm-commit">
                  {committing ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                  Confirm & commit
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Step 4 — Batch history & rollback ── */}
      <section className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden"
               data-testid="imp-history">
        <header className="px-5 py-3 bg-neutral-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} />
            <div className="font-display text-lg">Batch history</div>
          </div>
          <button className="text-xs underline" onClick={loadBatches}>Refresh</button>
        </header>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest bg-neutral-50">
            <tr>
              <th className="text-left px-3 py-2">Uploaded</th>
              <th className="text-left px-3 py-2">File</th>
              <th className="text-left px-3 py-2">Release</th>
              <th className="text-right px-3 py-2">Rows</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id} className="border-t border-neutral-200"
                  data-testid={`imp-batch-${b.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                <td className="px-3 py-2 font-mono">{b.source_filename}</td>
                <td className="px-3 py-2 font-mono">{releaseLabel(b.target_release_id)}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {b.valid_rows}/{b.total_rows}
                </td>
                <td className="px-3 py-2">
                  <StatusPill status={b.status} />
                </td>
                <td className="px-3 py-2 text-right">
                  {b.status === "committed" && (
                    <button className="text-xs text-red-800 underline"
                            onClick={() => doRollback(b)}
                            data-testid={`imp-rollback-${b.id}`}>
                      Rollback
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-6 text-center text-[var(--smoke)]">
                No import batches yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Small building blocks
// ═══════════════════════════════════════════════════════════════════
function SpinBox({ testid }) {
  return (
    <div className="p-10 flex items-center justify-center" data-testid={testid}>
      <Loader2 className="animate-spin" />
    </div>
  );
}
function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">{label}</span>
      <input type={type} value={value ?? ""}
             onChange={e => onChange(e.target.value)}
             className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
             data-testid={testid} />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">{label}</span>
      <select value={value ?? ""} onChange={e => onChange(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
              data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function Banner({ tone, children }) {
  const cls = tone === "error"
    ? "bg-red-50 border border-red-300 text-red-900"
    : tone === "success"
    ? "bg-emerald-50 border border-emerald-300 text-emerald-900"
    : "bg-neutral-100 border border-neutral-300";
  return (
    <div className={"rounded-lg px-4 py-3 text-sm flex items-start gap-2 " + cls}>
      {tone === "error" && <AlertCircle size={16} className="mt-0.5" />}
      <div>{children}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const cfg = {
    committed: ["bg-emerald-100 text-emerald-900", "Committed"],
    dry_run_ready: ["bg-yellow-100 text-yellow-900", "Dry-run ready"],
    rolled_back: ["bg-neutral-200 text-neutral-700", "Rolled back"],
    failed: ["bg-red-100 text-red-900", "Failed"],
    failed_validation: ["bg-red-100 text-red-900", "Validation failed"],
    pending: ["bg-neutral-100 text-neutral-800", "Pending"],
  }[status] || ["bg-neutral-100 text-neutral-800", status];
  return <span className={"px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest " + cfg[0]}>{cfg[1]}</span>;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || "Request failed.";
}
